import { AxiosError } from 'axios';
import {
  MultiFactorError,
  MultiFactorResolver,
  SAMLAuthProvider,
  UserCredential,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import { auth } from '../../api/firebase/config';
import { recordClientAuditEvent } from '../../api/firebase/function/security/clientAuditEventFunction';
import {
  completeSamlSignIn,
  getSamlSignInInfo,
} from '../../api/firebase/function/security/securitySamlFunction';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  orange60,
} from '../../constants/colors';
import {
  auditAction,
  auditResourceType,
  SAML_SSO_PREFETCH_DEBOUNCE_MS,
} from '../../constants/enum';
import { headerHeight, spHeaderHeight } from '../../constants/location';
import { EMAIL_INPUT_PLACEHOLDER } from '../../constants/placeholders';
import { AuthContext } from '../../contexts/auth';
import { resolveLoginRedirectPath } from '../../utils/loginRedirect';
import { authError, common } from '../../modules/error';
import { userLoginSchema } from '../../validation/user';
import type { SamlSignInInfo } from '../../types/security';
import type { UserLoginType } from '../../types/user';

import CustomTextfield from '../uiParts/form/CustomTextfield';
import Header from '../uiParts/menu/Header';
import PasswordTextField from '../uiParts/form/PasswordTextfield';
import CustomSnackbar from '../uiParts/form/CustomSnackbar';
import CustomLoading from '../uiParts/form/CustomLoading';
import MfaSignInDialog from '../uiParts/mfa/MfaSignInDialog';
import MetaTitle from '../utils/MetaTitle';

const styles = {
  button: {
    height: '44px',
    fontSize: '16px',
    fontWeight: '500',
  },
  ssoButton: {
    marginTop: '16px',
    color: gray100,
    borderColor: gray40,
    backgroundColor: white,
    // MUI 既定の uppercase 変換を無効化（「SSOでログイン」の表記を保つ）
    textTransform: 'none',
    '&:hover': {
      backgroundColor: white,
      borderColor: gray100,
    },
  },
  ssoDescription: {
    color: gray100,
    fontSize: '14px',
    mb: '24px',
    textAlign: 'left',
    width: '100%',
  },
  pageContainer: {
    pt: headerHeight,
    minHeight: '100vh',
    '@media screen and (max-width: 600px)': {
      pt: spHeaderHeight,
      minHeight: '100vh',
    },
  },
  loginFormContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '40px',
    width: '400px',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    '@media screen and (max-width:600px)': {
      minHeight: '100vh',
      width: '100%',
      padding: '0 16px',
      boxSizing: 'border-box',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  registerLink: {
    color: orange60,
    display: 'block',
    fontWeight: '500',
    mt: '36px',
    textAlign: 'center',
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

// SSO ボタンの表示ラベル（エラーメッセージ内でも参照する）
const SSO_LOGIN_LABEL = 'SSOでログイン';

// IdP のアカウント自動選択で入力と異なるアカウントが認証されたときのメッセージ（#3166）
const buildSsoEmailMismatchMessage = (signedInEmail: string): string =>
  `入力したメールアドレスと異なるアカウント（${signedInEmail}）で認証されました。IdP のアカウント選択画面で、入力したアカウントを選択してください。`;

// @firebase/auth 内部の signInWithIdp レスポンスのうち参照するフィールドだけの narrow な型。
// unknown で受けて実行時に検証するため、SDK 側の構造変化があっても安全に undefined へ落ちる
interface MfaErrorServerResponse {
  email?: unknown;
}

// multi-factor-auth-required エラーから認証済みアカウントのメールアドレスを取り出す（#3166）。
// 公開 API の MultiFactorError には認証されたメールアドレスが生えていないため、
// SDK が customData._serverResponse に保持している signInWithIdp のレスポンス
// （@firebase/auth の内部フィールド）から読み取る。二要素認証ダイアログを出す前に
// アカウント不一致を検出し、確認コード（TOTP）の無駄な消費を防ぐのが目的。
// SDK 更新等でフィールドが取得できない場合は undefined を返し、従来どおり
// 確認コード検証後の finishSsoSignIn の不一致チェックにフォールバックする
const getMfaSignedInEmail = (error: FirebaseError): string | undefined => {
  const serverResponse: unknown = error.customData?._serverResponse;
  if (typeof serverResponse !== 'object' || serverResponse === null) return undefined;
  const { email } = serverResponse as MfaErrorServerResponse;
  return typeof email === 'string' && email !== '' ? email : undefined;
};

const AuthLogin: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  // SSO モード（#3021）: Notion 風に「SSOでログイン」押下でメールアドレス欄のみの表示に切り替える
  const [ssoMode, setSsoMode] = useState(false);
  // 二要素認証（#2984）: multi-factor-auth-required 時の resolver
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  // 二要素認証ダイアログを SSO 経由で開いているか（#3166）。
  // SSO の場合は確認コード検証後に SAML の後処理（JIT・遷移）を続ける必要がある
  const [mfaFromSso, setMfaFromSso] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserLoginType>({
    resolver: yupResolver(userLoginSchema),
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        Boolean(errors.email) ||
        Boolean(errors.password)
      );
    }
    return false;
  };

  // ログイン処理
  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const userAuth = await signInWithEmailAndPassword(auth, watch('email'), watch('password'));
      if (!userAuth) throw new Error(authError.signInFailed);
      // 監査ログ（#2985）: ログイン成功。fire-and-forget で画面遷移を遅らせない
      void recordClientAuditEvent({
        action: auditAction.login,
        resource_type: auditResourceType.session,
      });
      // 認証成功時: loadingはtrueのまま維持（AuthContextのuser更新→navigate完了まで）
    } catch (error: unknown) {
      setLoading(false);
      // 二要素認証が有効なユーザーは確認コード入力ダイアログへ（#2984）
      if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError));
        return;
      }
      let errorText: string;
      if (error instanceof FirebaseError) {
        // Firebase認証エラー：Sentryに送信しない（ユーザー操作ミスの可能性が高い）
        errorText = getErrorMessageFromAuthErrorCode(error.code);
        // 監査ログ（#2985）: ログイン失敗。未認証のため email で対象ユーザーを特定する
        void recordClientAuditEvent({
          action: auditAction.loginFailed,
          resource_type: auditResourceType.session,
          email: watch('email'),
        });
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        // 予期しないエラー：Sentryに送信する
        Sentry.captureException(error);
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    }
  };

  useEffect(() => {
    // ログイン状態の場合は遷移先へ移動する。
    // 未ログインで保護ページを直接開いた場合は ?redirect= に元URLが入るため、そこへ復帰する（#3184）。
    // 指定がなければ従来どおりホーム画面へ遷移する。
    if (user) navigate(resolveLoginRedirectPath(location.search), { replace: true });
  }, [user, location.search, navigate]);

  // SSO プロバイダ解決（getSamlSignInInfo）の先行実行キャッシュ。
  // クリックからポップアップまでの間に Callable の await を挟むと、コールドスタート時に
  // Chrome の transient user activation（5秒）が失効して signInWithPopup がブロックされる
  // （auth/popup-blocked）ため、メール入力の時点でドメイン解決を済ませておく。
  const ssoPrefetch = useRef<{ email: string; promise: Promise<SamlSignInInfo> } | null>(null);
  const prefetchSsoSignInInfo = useCallback((email: string): Promise<SamlSignInInfo> => {
    const cached = ssoPrefetch.current;
    if (cached && cached.email === email) return cached.promise;
    const promise = getSamlSignInInfo(email);
    ssoPrefetch.current = { email, promise };
    // 失敗した結果はキャッシュに残さず、次回クリック時に再取得させる
    promise.catch(() => {
      if (ssoPrefetch.current?.promise === promise) ssoPrefetch.current = null;
    });
    return promise;
  }, []);

  const emailValue = watch('email');
  useEffect(() => {
    const email = (emailValue ?? '').trim();
    // メールアドレスの形が完成したものだけ先行解決する（入力途中の無駄な呼び出しを避ける）
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return undefined;
    const timer = window.setTimeout(() => {
      void prefetchSsoSignInInfo(email);
    }, SAML_SSO_PREFETCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [emailValue, prefetchSsoSignInInfo]);

  // IdP 認証が完了したあとの共通後処理（#3021）。
  // 二要素認証を挟んだ場合（#3166）も確認コード検証後にここへ合流する。
  const finishSsoSignIn = async (inputEmail: string, credential: UserCredential): Promise<void> => {
    // IdP 側のアカウント自動選択で、入力したメールと別のアカウントが認証されることがある
    // （SAML には login_hint 相当が無い）。意図しないユーザーでのログインを防ぐため一致を検証する
    const signedInEmail = (credential.user.email ?? '').toLowerCase();
    if (signedInEmail !== inputEmail.toLowerCase()) {
      throw new Error(buildSsoEmailMismatchMessage(signedInEmail));
    }
    // JIT プロビジョニング（アカウント自動作成）後は AuthContext を再初期化するため
    // フルリロードでホームへ遷移する
    await completeSamlSignIn();
    // JIT で設定されたカスタムクレーム（company_id / role）を ID トークンに反映する
    await auth.currentUser?.getIdToken(true);
    // 監査ログ（#2985）: SAML 経由のログイン成功（#3122）。
    // 直後に window.location.replace でフルリロードするため、fire-and-forget では
    // 記録が中断される。await して完了を待つ
    await recordClientAuditEvent({
      action: auditAction.login,
      resource_type: auditResourceType.session,
    });
    window.location.replace('/home');
  };

  // SSO ログインの失敗処理（#3021）。二要素認証の要求だけは失敗ではなくダイアログへ進む（#3166）
  const handleSsoSignInError = async (error: unknown): Promise<void> => {
    setLoading(false);
    // 二要素認証が有効なユーザーは確認コード入力ダイアログへ（#3166）。
    // resolver が第一要素の証明（mfaPendingCredential）を保持しているため、
    // 他の失敗と違ってここで signOut してはいけない
    if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
      // ダイアログを出す前にアカウント不一致を検出する（#3166）。不一致のままダイアログへ
      // 進むと、確認コードを1回無駄に消費した後で finishSsoSignIn に弾かれてしまうため。
      // MFA 未解決の時点ではセッションが確立していないので signOut は不要
      const signedInEmail = getMfaSignedInEmail(error);
      const inputEmail = (watch('email') ?? '').trim().toLowerCase();
      if (signedInEmail !== undefined && signedInEmail.toLowerCase() !== inputEmail) {
        setErrorText(buildSsoEmailMismatchMessage(signedInEmail.toLowerCase()));
        setOpen(true);
        return;
      }
      setMfaFromSso(true);
      setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError));
      return;
    }
    // JIT 失敗（担当者数上限・ドメイン不一致等）は中途半端なセッションを残さない
    await signOut(auth).catch(() => undefined);
    if (
      error instanceof FirebaseError &&
      (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request')
    ) {
      // ユーザー自身の操作（ポップアップを閉じた・ボタン連打）はエラー扱いしない
      return;
    }
    if (error instanceof FirebaseError && error.code === 'auth/popup-blocked') {
      // プリフェッチ前のクリック＋コールドスタートが重なった場合の保険。
      // 再クリック時はプロバイダ解決がキャッシュ済みのため即時にポップアップが開く
      setErrorText(
        `ポップアップの表示がブロックされました。もう一度「${SSO_LOGIN_LABEL}」を押してください。`,
      );
      setOpen(true);
      return;
    }
    if (error instanceof FirebaseError && error.code.startsWith('functions/')) {
      // Callable（completeSamlSignIn 等）の HttpsError はユーザー向けメッセージを
      // そのまま返しているため表示する（担当者数上限・ドメイン不一致等）
      setErrorText(error.message);
    } else if (error instanceof FirebaseError) {
      setErrorText(getErrorMessageFromAuthErrorCode(error.code));
    } else if (error instanceof Error && error.message) {
      setErrorText(error.message);
    } else {
      Sentry.captureException(error);
      setErrorText(common);
    }
    setOpen(true);
  };

  // SSO 経由の二要素認証が成功したあとの続き（#3166）。
  // 確認コードの検証でログインが完了しているため、SAML の後処理へ合流する
  const onSsoMfaSuccess = async (credential: UserCredential): Promise<void> => {
    const email = (watch('email') ?? '').trim();
    setMfaResolver(null);
    setMfaFromSso(false);
    try {
      setLoading(true);
      await finishSsoSignIn(email, credential);
    } catch (error: unknown) {
      await handleSsoSignInError(error);
    }
  };

  // SSO（SAML）サインイン開始。メールのドメインからテナントのプロバイダを解決して
  // IdP（Microsoft Entra ID / Google Workspace）へポップアップで認証する。
  // IdP はテナント設定から自動解決されるため、ユーザーが選択する必要はない。
  // リダイレクト方式はアプリと authDomain が別ドメインの場合に Chrome の
  // サードパーティストレージ分離でセッションが引き継がれないため使用しない
  // （Firebase 公式の signInWithRedirect ベストプラクティスに準拠）。
  const onSsoSignIn = async (): Promise<void> => {
    const email = (watch('email') ?? '').trim();
    if (!email || !email.includes('@')) {
      setErrorText(`メールアドレスを入力してから「${SSO_LOGIN_LABEL}」を押してください。`);
      setOpen(true);
      return;
    }
    try {
      setLoading(true);
      const info = await prefetchSsoSignInInfo(email);
      if (!info.available || !info.provider_id) {
        setLoading(false);
        setErrorText('このメールアドレスのドメインでは SSO が有効化されていません。');
        setOpen(true);
        return;
      }
      const result = await signInWithPopup(auth, new SAMLAuthProvider(info.provider_id));
      if (!result) throw new Error(authError.signInFailed);
      await finishSsoSignIn(email, result);
    } catch (error: unknown) {
      await handleSsoSignInError(error);
    }
  };

  return (
    <Typography component="div" sx={styles.pageContainer}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography
          component="form"
          sx={styles.loginFormContainer}
          onSubmit={async (e: React.FormEvent): Promise<void> => {
            e.preventDefault();
            if (ssoMode) {
              await onSsoSignIn();
              return;
            }
            setFirstSubmit(true);
            await handleSubmit(async () => onSubmit())();
          }}
        >
          <Typography sx={styles.title}>{ssoMode ? SSO_LOGIN_LABEL : 'ログイン'}</Typography>
          {ssoMode && (
            <Typography sx={styles.ssoDescription}>
              組織で設定された ID
              プロバイダでログインします。組織のメールアドレスを入力してください。
            </Typography>
          )}
          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.email)}
                formMb="24px"
                helperText={errors.email?.message}
                labelText="メールアドレス"
                placeholder={EMAIL_INPUT_PLACEHOLDER}
                requiredText="必須"
                fullWidth
              />
            )}
            control={control}
            name="email"
          />

          {!ssoMode && (
            <Controller
              render={({ field }): JSX.Element => (
                <PasswordTextField
                  {...field}
                  error={Boolean(errors.password)}
                  formMb="40px"
                  helperText={errors.password?.message}
                  labelText="パスワード"
                  requiredText="必須"
                />
              )}
              control={control}
              name="password"
            />
          )}

          {ssoMode ? (
            <Button
              fullWidth
              type="submit"
              sx={[primaryButtonBackgroundColor, styles.button]}
              variant="contained"
            >
              {SSO_LOGIN_LABEL}
            </Button>
          ) : (
            <>
              <Button
                disabled={buttonDisabledFlag()}
                fullWidth
                type="submit"
                sx={[primaryButtonBackgroundColor, styles.button]}
                variant="contained"
              >
                メールアドレスでログイン
              </Button>

              <Button
                fullWidth
                onClick={(): void => setSsoMode(true)}
                sx={[styles.ssoButton, styles.button]}
                variant="outlined"
              >
                {SSO_LOGIN_LABEL}
              </Button>
            </>
          )}

          {ssoMode ? (
            <Link
              color="inherit"
              component="button"
              type="button"
              onClick={(): void => setSsoMode(false)}
              sx={[styles.link, { width: 'fit-content' }]}
            >
              ← 他の方法でログイン
            </Link>
          ) : (
            <Link
              color="inherit"
              href="/auth/password_reset"
              sx={[styles.link, { width: 'fit-content' }]}
            >
              パスワードをお忘れの方はこちら
            </Link>
          )}
        </Typography>
        <Link color="inherit" href="/auth/register" sx={styles.registerLink}>
          新規会員登録（無料）はこちら
        </Link>
      </Typography>
      <MfaSignInDialog
        open={mfaResolver !== null}
        resolver={mfaResolver}
        email={watch('email') ?? ''}
        password={watch('password') ?? ''}
        // SSO ユーザーはパスワードを持たないためバックアップコードでの第一要素再検証ができない（#3166）
        backupCodeAvailable={!mfaFromSso}
        onSuccess={mfaFromSso ? onSsoMfaSuccess : undefined}
        onClose={(): void => {
          setMfaResolver(null);
          setMfaFromSso(false);
        }}
      />
      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Typography>
  );
};

export default AuthLogin;
